import {
  BookOpenIcon,
  ChatBubbleBottomCenterIcon,
  EnvelopeIcon,
  EnvelopeOpenIcon,
  FunnelIcon,
  PencilIcon,
  PhoneIcon,
  PlayIcon,
  TrashIcon,
  UserPlusIcon,
} from "@heroicons/react/24/outline";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import Button from "../../components/Button";
import { ConfirmationModal } from "../../components/ConfirmationModal";
import FilteredTable, { TableFilter } from "../../components/FilteredTable";
import { LoadingMask } from "../../components/LoadingMask";
import { Toast } from "../../components/Toast";
import {
  ConversationsPath,
  NewClientPath,
  UpdateClientPath,
} from "../../constants/Routes";
import { RoleEnum } from "../../enums/Role";
import { Client } from "../../models/Client";
import {
  deleteClient,
  fetchClients,
  pastPage,
  setClientsActiveTab,
} from "../../state/clients/actions";
import { AppDispatch, RootState } from "../../state/store";
import {
  createConversation,
  fetchConversationMessages,
} from "../../state/twilio/actions";
import { shortLocalDateFormat } from "../../utils/functions";
import { FilterModal } from "./FilterModal";
import { ConversationType } from "../../enums/ConversationType";
import { BellAlertIcon } from "@heroicons/react/24/solid";
import moment from "moment";
import { SentMailConfirmationModal } from "../../components/clients/mail/SentMailConfirmationModal";
import { checkMailClientAddress, createMailClient } from "../../state/mail/actions";
import { SuccessErrorModal } from "../../components/SuccessErrorModal";
import { MessageTypeEnum } from "../../enums/MessageType";
import { Address } from "../../models/Address";
import { SentMailSelectTypeModal } from "../../components/clients/mail/SentMailSelectTypeModal";
import { MailType } from "../../models/Mail";
import { motion } from "framer-motion";
import { User } from "../../models/User";
import { AgentBackUpClient, AgentBackUpClientLabel } from "../../enums/AgentBackUp";
import { BackUpOptions } from "../../enums/BackupOptions";
import { FrequencyFormModel } from "../../models/FollowUp";
import { AssignFrequencyAutomationModal } from "../Automations/AssignFrequencyAutomationModal";
import { assingFollowUpMessage } from "../../state/automations/action";
import { Device } from "@twilio/voice-sdk";

interface Props {
  makePhoneCall: (phone: string) => void;
}

const tabs = [AgentBackUpClient.MY_CLIENTS, AgentBackUpClient.BACKUP_CLIENTS];

export const Clients = (props: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const pageClients = useSelector((state: any) => state.clients.pageClients);
  const paginatedOptions = useSelector(
    (state: any) => state.clients.paginatedOptions
  );
  const filters = useSelector((state: any) => state.clients.filters);
  const error = useSelector((state: any) => state.clients.error);
  const isLoading = useSelector((state: any) => state.clients.isLoading);
  const users = useSelector((state: any) => state.users.users);
  const conversations = useSelector((state: any) => state.twilio.conversations);
  const currentUser = useSelector((state: any) => state.users.currentUser);
  const deviceState:Device.State = useSelector(
    (state: any) => state.twilio.twilioDeviceState
  );
  const twilioChatState = useSelector(
    (state: any) => state.twilio.areTwilioCredentialsValid
  );
  const [selectedAgentIds, setSelectedAgentIds] = useState([]);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [isConfirmationLoading, setIsConfirmationLoading] = useState(false);
  const [seletedClient, setSelectedClient] = useState<Client | null>(null);
  const [isFilterModalOpen, setFilterModalOpen] = useState<boolean>(false);
  const [showError, setShowError] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const todayReminders = useSelector((state: RootState) => state.reminders.todayReminders);

  const isSendMailLoading = useSelector((state: RootState) => state.mails.isLoading);
  const mailError = useSelector((state: any) => state.mails.error);
  const agencyMailTypes = useSelector((state: RootState) => state.mails.agencyMailTypes)
  const [showMailError, setShowMailError] = useState(false);
  const [seletedAddress, setSelectedAddress] = useState<Address | null>(null);
  const [errorMailMessage, setErrorMailMessage] = useState<string | null>(null);

  const [ showSentMailConfirmationModal, setShowSentMailConfirmationModal] = useState<{
    client?: Client;
    show: boolean;
  }>({ show: false, client: undefined });

  const [showSuccessErrorModal, setShowSuccessErrorModal] = useState<{
    show: boolean;
    messageType: number;
    message: string;
    failedButtonMessage?: string;
  }>({ show: false, messageType: 2, message: "" });

  const [sentMailSelectTypeModal, setSentMailSelectTypeModal] = useState<{
    show: boolean;
    agencyMailTypes?: any;
  }>({ show: false, agencyMailTypes: [] });

  const clientsActiveTab = useSelector((state: RootState) => state.clients.clientActiveTab);
  const [activeTab, setActiveTab] = useState(clientsActiveTab ?? AgentBackUpClient.MY_CLIENTS);
  const backupOfAgents = useSelector((state: RootState) => state.users.backupOfAgents);
  const [canEditClient, setCanEditClient] = useState<boolean>()

  const [assignFrequencyAutomationModal, setAssignFrequencyAutomationModal] = useState<{
    show: boolean;
    client?: Client;
  }>({ show: false, client: undefined });

  const [infoFilterModal, setInfoFilterModal] = useState<{
    myClients: boolean;
    backupOfAgents: User[];
  }>({ myClients: clientsActiveTab === AgentBackUpClient.MY_CLIENTS, backupOfAgents: clientsActiveTab === AgentBackUpClient.MY_CLIENTS ? [] : backupOfAgents });

  useEffect(() => {
    if (showError) {
      Toast(t(error?.reason ?? "SOMETHING_WENT_WRONG"), t(error?.cause_info));
      setShowError(false);
    }
  }, [showError, error, t]);

  useEffect(() => {
    if (showMailError) {
      if(mailError?.cause_info) {
        if(mailError?.cause_info === "CLIENT_ALREADY_IN_MAIL_LIST") {
          handleShowSuccessErrorModal(
            MessageTypeEnum.FAILED,
            t(mailError?.cause_info),
            t("ACCEPT")
          );
        } else {
          handleShowSuccessErrorModal(
            MessageTypeEnum.FAILED,
            t(mailError?.cause_info),
            t("UPDATE_CLIENT_ADDRESS")
          );
        }
        setErrorMailMessage(mailError?.cause_info)
      } else {
        handleShowSuccessErrorModal(
          MessageTypeEnum.FAILED,
          t("SOMETHING_WENT_WRONG"),
          t("UPDATE_CLIENT_ADDRESS")
        );
        setErrorMailMessage(null)
      }
      
    }
  }, [showMailError, mailError, t]);

  useEffect(() => {
    if(currentUser?.role_id === RoleEnum.AGENT) {
      if(clientsActiveTab === AgentBackUpClient.BACKUP_CLIENTS) {
        if(currentUser?.agency && currentUser?.agency.backup_config_id === BackUpOptions.ONLY_READ) {
          setCanEditClient(false);
        } else {
          setCanEditClient(true);
        }
      } else {
        setCanEditClient(true);
      }
    } else {
      setCanEditClient(true);
    }
  }, [currentUser]);

  const agents = useMemo(() => {
    return users.map((x: any) => {
      return {
        label: `${x.first_name} ${x.last_name}`,
        value: x.id,
      };
    });
  }, [users]);

  const openConfirmationModal = (client: any) => {
    setIsConfirmationModalOpen(true);
    setSelectedClient(client);
  };

  const closeConfirmationModal = () => {
    setIsConfirmationModalOpen(false);
    setSelectedClient(null);
  };

  const makeConversation = useCallback(
    (phone: string) => {
      if (conversations && currentUser) {
        const selectedConversation = conversations.find(
          (x: any) => x.friendlyName === phone
        );
        if (selectedConversation) {
          dispatch(
            fetchConversationMessages({
              conversationSid: selectedConversation.sid,
            })
          );
        } else {
          dispatch(
            createConversation({
              newConversation: {
                from: currentUser.main_twilio_number?.twilio_phone,
                to: phone,
              },
              conversationType: ConversationType.CLIENTS,
            })
          );
        }
        navigate(ConversationsPath);
      }
    },
    [conversations, currentUser, dispatch, navigate]
  );

  const clientHasAReminder = (client: Client) => {
    const todayHour = moment(new Date());
    let reminder = todayReminders.find((r) => r.client_id === client.id);
    if (reminder !== undefined) {
      const reminderHour = moment(reminder.date);
      return reminderHour.isSameOrAfter(todayHour);
    } else {
      return false;
    }
  }

  const handleRowClick = (client: any) => {
    updateClient(client);
  };

  const renderClickableCell = (value: any, row: any) => (
    <div
      onClick={() => handleRowClick(row.row.original)}
      className="w-full h-full block px-2 py-1 cursor-pointer hover:bg-gray-100"
    >
      {value}
    </div>
  );

  const columns = useMemo(() => {
    const showColumns = [
      {
        Header: t("NAME"),
        accessor: "first_name",
        Cell: (row: any) => renderClickableCell(row.value, row),
      },
      {
        Header: t("LAST_NAME"),
        accessor: "last_name",
        Cell: (row: any) => renderClickableCell(row.value, row),
      },
      {
        Header: t("PHONE"),
        accessor: "phone",
        Cell: (row: any) => renderClickableCell(row.value, row),
      },
      {
        Header: t("EMAIL"),
        accessor: "email",
        Cell: (row: any) => renderClickableCell(row.value, row),
      },
      {
        Header: t("CREATION_DATE"),
        Cell: (row: any) => {
          const formattedDate = shortLocalDateFormat(`${row.row.original.creation_date}`);
          return renderClickableCell(formattedDate, row);
        },
      },
      {
        Header: t("AGENT"),
        Cell: (row: any) => {
          const { agent } = row.row.original;
          const agentName = `${agent?.first_name ?? ""} ${agent?.last_name ?? ""}`;
          return renderClickableCell(agentName, row);
        },
      },
    ];

    const actions = {
      Header: " ",
      Cell: (row: any) => {
        return (
          <div className="flex flex-row justify-center divide-x">
            <Button
              isTerciary
              /*isDisabled={canRunAutomation(row.row.original)}*/
              iconOnly
              tooltip={t("ASSIGN_FREQUENCY_AUTOMATION")}
              onClick={() => openSelectFrequencyAutomationModal(row.row.original)}
              classNames="mr-2"
            >
              <PlayIcon className="h-5 aspect-square stroke-gold" />
            </Button>
            <Button
              isTerciary
              isDisabled={!twilioChatState}
              tooltip={t("START_CONVERSATION")}
              iconOnly
              onClick={() => makeConversation(row.row.original.phone)}
              classNames="pl-2"
            >
              <ChatBubbleBottomCenterIcon className="h-5 aspect-square text-gold" />
            </Button>
            <Button
              isTerciary
              isDisabled={deviceState !== Device.State.Registered}
              tooltip={t("CALL_CLIENT")}
              iconOnly
              onClick={() => props.makePhoneCall(row.row.original.phone)}
              classNames="pl-2"
            >
              <PhoneIcon className="h-5 aspect-square text-gold" />
            </Button>
            <Button
              isTerciary
              tooltip={t("SEND_MAIL")}
              iconOnly
              onClick={() => setShowSentMailConfirmationModal({ show: true, client: row.row.original })}
              classNames="mr-2"
            >
              <EnvelopeIcon className="h-5 aspect-square text-blue" />
            </Button>
            <Button
              isTerciary
              tooltip={t("UPDATE_CLIENT")}
              iconOnly
              onClick={() => updateClient(row.row.original)}
              classNames="mr-2"
            >
              <PencilIcon className="h-5 aspect-square text-blue" />
            </Button>
            <Button
              isTerciary
              tooltip={t("DELETE_CLIENT")}
              iconOnly
              onClick={() => openConfirmationModal(row.row.original)}
              classNames="mr-2"
            >
              <TrashIcon className="h-5 aspect-square text-red-600" />
            </Button>
          </div>
        );
      },
    };

    const backupActions = {
      Header: " ",
      Cell: (row: any) => {
        return (
          <div className="flex flex-row justify-center divide-x">
            <Button
              isTerciary
              tooltip={t("CLIENT_INFORMATION")}
              iconOnly
              onClick={() => updateClient(row.row.original)}
              classNames="mr-2"
            >
              <BookOpenIcon className="h-5 aspect-square text-blue" />
            </Button>
          </div>
        );
      },
    };
    
    if(!canEditClient) {
      return [...showColumns, backupActions];
    }

    return [...showColumns, actions];
  }, [deviceState, makeConversation, props, t, currentUser, todayReminders, canEditClient]);

  const updateClient = (row: any, fromSendMail: boolean = false) => {
    navigate(UpdateClientPath, { state: { client: row, fromSendMail: fromSendMail } });
  };

  const agentsFilter = () => {
    return currentUser?.role_id !== RoleEnum.AGENT
      ? ({
        label: t("AGENTS"),
        options: agents,
        name: "agent",
        setOption: setSelectedAgentIds,
      } as TableFilter)
      : undefined;
  };

  const showDeleteConfirmationModal = () => {
    return (
      <ConfirmationModal
        isDeleteConfirm={true}
        isOpen={isConfirmationModalOpen}
        onClose={closeConfirmationModal}
        label={t("DELETE_CLIENT")}
        saveButton={{
          label: t("DELETE"),
          icon: <TrashIcon />,
          onClick: onDeleteClient,
        }}
      >
        {isConfirmationLoading && <LoadingMask />}
        <div className="w-full flex flex-wrap items-center justify-center">
          <label className="text-lg font-semibold py-4">
            {t("DELETE_CLIENT_MESSAGE")}
          </label>
        </div>
      </ConfirmationModal>
    );
  };

  const onDeleteClient = () => {
    setIsConfirmationLoading(true);

    dispatch(
      deleteClient({
        clientId: seletedClient!.id!,
        page: paginatedOptions.page,
        pageSize: paginatedOptions.pageSize,
      })
    ).then((e) => {
      if (e.type === "clients/deleteClient/rejected") {
        setIsConfirmationLoading(false);
        setShowError(true);
      } else {
        toast(t("SUCCESSFULLY_DELETE_CLIENT"));
        setIsConfirmationLoading(false);
        closeConfirmationModal();
      }
    });
  };

  const getPaginationOptions = useMemo(() => {
    return {
      page: paginatedOptions.page,
      pageSize: paginatedOptions.pageSize,
      totalPages: paginatedOptions.totalPages,
      totalCount: paginatedOptions.totalCount,
      setPage: (value: number) => {
        value < paginatedOptions.page
          ? dispatch(pastPage())
          : dispatch(
            fetchClients({
              page: value,
              pageSize: paginatedOptions.pageSize,
              filters: filters,
              changeClients: false
            })
          );
      },
      setPageSize: (value: number) =>
        dispatch(
          fetchClients({
            page: paginatedOptions.page,
            pageSize: value,
            filters: filters,
            changeClients: false
          })
        ),
    };
  }, [dispatch, paginatedOptions]);

  // Función de resaltado para clientes
  const highlightClientRow = (client: Client) => {
    return clientHasAReminder(client); // Resalta filas si tiene un recordatorio
  };

  const onCheckClientAddressMail = (client: Client) => {
    dispatch(checkMailClientAddress({clientId: client.id!})).then((e) => {
      if (e.type === "mail/checkMailClientAddress/rejected") {
        setShowMailError(true);
        setSelectedClient(client);
      } else {
        let clientAddress = (e.payload || {}) as Address;
        setSelectedAddress(clientAddress);
        setSelectedClient(client);
        setSentMailSelectTypeModal({ show: true, agencyMailTypes: getMailTypes })
      }
    });
  };

  const onSelectedMailType = (mailType: MailType) => {
    if(seletedClient && seletedAddress) {
      onSentClientMail(seletedClient, seletedAddress, mailType);
    }
  }

  const onSentClientMail = (client: Client, address: Address, mailType: MailType) => {
    let name = client.first_name;
    if (client.middle_name !== undefined) {
        name = name + " " + client.middle_name;
    }
    name = name + " " + client.last_name;
    if (client.second_last_name) {
        name = name + " " + client.second_last_name;
    }

    const data = {
      client_id: client.id,
      full_name: name,
      phone: client.phone,
      address_line_1: address.address_line_1,
      address_line_2: address.address_line_2,
      city: address.city,
      state: address.state,
      zip_code: address.zip_code,
      country: address.country,
      mail_type_id: mailType.id
    };

    dispatch(createMailClient({newMailClient: data})).then((e) => {
      if (e.type === "mail/createMailClient/rejected") {
        setShowMailError(true);
      } else {
        handleShowSuccessErrorModal(
          MessageTypeEnum.SUCCESS,
          t("SUCCESSFULLY_SENT_CLIENT_MAIL")
        );
      }
    });
  };

  const handleShowSuccessErrorModal = (messageType: number, message: string, failedButtonMessage?: string) => {
    setShowSuccessErrorModal({
      show: true,
      messageType: messageType,
      message: message,
      failedButtonMessage: failedButtonMessage
    });
  };

  const handleCloseSuccessErrorModal = () => {
    if (showSuccessErrorModal.messageType === MessageTypeEnum.SUCCESS) {
      setShowSuccessErrorModal({
        show: false,
        messageType: MessageTypeEnum.SUCCESS,
        message: "",
      });
      
      setSelectedAddress(null);
      setSelectedClient(null);
    } else if (showSuccessErrorModal.messageType === MessageTypeEnum.FAILED) {
      setShowSuccessErrorModal({
        show: false,
        messageType: MessageTypeEnum.FAILED,
        message: "",
      });
      setShowMailError(false);
      setSelectedAddress(null);
      setSelectedClient(null);

      if(errorMailMessage !== null) {
        if(errorMailMessage !== "CLIENT_ALREADY_IN_MAIL_LIST") {
          updateClient(seletedClient, true);
        }
      }
    }
  };

  const getMailTypes = useMemo(() => {
    return agencyMailTypes.map((a: any) => {
      return {
        label: `${a.name}`,
        value: `${a.id}`,
        key: a.id,
      };
    });
  }, [agencyMailTypes]);

  const onHandleChangeTab = (tab: number) => {
    if(tab !== activeTab) {
      setActiveTab(tab);
      if(tab === AgentBackUpClient.MY_CLIENTS) {
        setInfoFilterModal({ myClients: true, backupOfAgents: [] });
        dispatch(fetchClients({ page: 0, pageSize: 10, filters: {agents: [currentUser?.id]}, changeClients: true })).then((e) => {
          if (e.type === "clients/getClients/rejected") {
            setShowError(true);
          } else {
            setCanEditClient(true);
          }
        })
        dispatch(setClientsActiveTab(tab));

      } else {
        setInfoFilterModal({ myClients: false, backupOfAgents: backupOfAgents });
        const filterAgents = backupOfAgents.map((a) => {return a.id!});
        dispatch(fetchClients({ page: 0, pageSize: 10, filters: {agents: filterAgents}, changeClients: true })).then((e) => {
          if (e.type === "clients/getClients/rejected") {
            setShowError(true);
          } else {
            if(currentUser?.agency && currentUser?.agency.backup_config_id === BackUpOptions.ONLY_READ) {
              setCanEditClient(false);
            } else {
              setCanEditClient(true);
            }
          }
        })
        dispatch(setClientsActiveTab(tab));

      }
    }
  }

  const openSelectFrequencyAutomationModal = (client: Client) => {
    setAssignFrequencyAutomationModal({ show: true, client: client });
  }

  return (
    <>
      {(isLoading || isSendMailLoading) && <LoadingMask />}
      {showDeleteConfirmationModal()}
      <FilteredTable
        columns={columns}
        filter={agentsFilter()}
        data={pageClients}
        highlightRow={highlightClientRow}
        isPaginated
        paginationOptions={getPaginationOptions}
        hasSearchBar={false}
        secondaryActionButton={
          <Button onClick={() => navigate(NewClientPath)}>
            <span className="flex items-center font-semibold pr-3">
              <div className="w-8 p-1 aspect-square mr-2">
                <UserPlusIcon />
              </div>
              {t("NEW_CLIENT")}
            </span>
          </Button>
        }
        buttonAction={
          <Button
            onClick={() => setFilterModalOpen(true)}
            iconOnly
            classNames="w-9 p-1 aspect-square mr-4 "
          >
            <FunnelIcon className=""></FunnelIcon>
          </Button>
        }
        quaternaryActionButton={
          currentUser && currentUser?.role_id === RoleEnum.AGENT && backupOfAgents.length > 0
          ? <div className="relative flex border border-gray-300 rounded-lg w-64 p-1 bg-neutral-200">
              {tabs.map((tab) => (
                <button
                  key={tab}
                  onClick={() => onHandleChangeTab(tab)}
                  className={`relative flex-1 text-center py-2 text-sm font-medium transition-colors ${activeTab === tab ? "text-black" : "text-gray-400"
                    }`}
                >
                  {activeTab === tab && (
                    <motion.div
                      layoutId="tab-indicator"
                      className="absolute inset-0 bg-white rounded-md shadow"
                    />
                  )}
                  <span className="relative">{AgentBackUpClientLabel(`${tab}`)}</span>
                </button>
              ))}
            </div>
          : <div></div>
        }
      />
      <FilterModal
        info={infoFilterModal}
        isFilterModalOpen={isFilterModalOpen}
        setFilterModalOpen={() => setFilterModalOpen(false)}
      />
      <AssignFrequencyAutomationModal 
        info={assignFrequencyAutomationModal}
        onClose={() => setAssignFrequencyAutomationModal({ show: false, client: undefined })}
      />
      <SentMailConfirmationModal
        info={showSentMailConfirmationModal}
        onClose={() => setShowSentMailConfirmationModal({show : false, client: undefined})}
        sentMail={(client: Client) => onCheckClientAddressMail(client)}
      />
      <SuccessErrorModal
        info={showSuccessErrorModal}
        onClose={handleCloseSuccessErrorModal}
      />
      <SentMailSelectTypeModal
        info={sentMailSelectTypeModal}
        onSubmited={(selectedType) => {
          const mType = agencyMailTypes.find((e: any) => e.id === +selectedType);
          if(mType) {
            onSelectedMailType(mType);
          }
        }}
        onClose={() => setSentMailSelectTypeModal({ show: false, agencyMailTypes: [] })}
      />
      <ToastContainer progressStyle={{ background: "#D4AF37" }} />
    </>
  );
};
